import { useCallback } from 'react';

import { todoCardIds } from '../../todo-cards/todo-card-ids';

/**
 * Hook for generating returns todos with visibility and completion properties
 * @param {object} params - Parameters for the hook
 * @param {boolean} params.hasShopifyStores - Whether there are Shopify stores
 * @param {boolean} params.hasNonShopifyStores - Whether there are non-Shopify stores
 * @param {boolean} params.hasUnsetupShopifyStores - Whether there are unsetup Shopify stores
 * @param {boolean} params.hasNotSetupLegacyStores - Whether there are not setup legacy stores
 * @param {boolean} params.hasOnlyManualStores - Whether there are only manual stores
 * @param {Array} params.todosMappedWithCheckedProp - Current todos with checked properties
 * @param {(todos: Array) => void} params.setTodosInLocalState - Function to set todos in local state
 * @param {(stepKey: string, todos: Array) => void} params.updateFTUWizardSellerSetting - Function to update FTU wizard seller setting
 * @param {string} params.currentStepKey - Current step key
 * @returns {() => void} handleReturnsTodoGeneration function
 */
export const useReturnsTodoGeneration = ({
  hasShopifyStores,
  hasNonShopifyStores,
  hasUnsetupShopifyStores,
  hasNotSetupLegacyStores,
  hasOnlyManualStores,
  todosMappedWithCheckedProp,
  setTodosInLocalState,
  updateFTUWizardSellerSetting,
  currentStepKey,
}) => {
  const handleReturnsTodoGeneration = useCallback(() => {
    const shouldAddShopifyTodo = hasShopifyStores && hasUnsetupShopifyStores;
    const shouldAddBrandedTodo = hasNonShopifyStores && (hasNotSetupLegacyStores || hasOnlyManualStores);

    const newTodos = [];

    if (shouldAddShopifyTodo) {
      newTodos.push({
        id: todoCardIds.SHOPIFY_RETURNS,
        isVisible: true,
        isComplete: false,
      });
    }

    if (shouldAddBrandedTodo) {
      newTodos.push({
        id: todoCardIds.BRANDED_RETURNS,
        isVisible: true,
        isComplete: false,
      });
    }

    const existingTodoIds = todosMappedWithCheckedProp.map((todo) => todo.id);
    const todosToAdd = newTodos.filter((todo) => !existingTodoIds.includes(todo.id));

    if (todosToAdd.length > 0) {
      const updatedTodos = [...todosMappedWithCheckedProp, ...todosToAdd];
      setTodosInLocalState(updatedTodos);
      updateFTUWizardSellerSetting(currentStepKey, updatedTodos);
    }
  }, [
    hasShopifyStores,
    hasUnsetupShopifyStores,
    hasNonShopifyStores,
    hasNotSetupLegacyStores,
    hasOnlyManualStores,
    todosMappedWithCheckedProp,
    setTodosInLocalState,
    updateFTUWizardSellerSetting,
    currentStepKey,
  ]);

  return handleReturnsTodoGeneration;
};
